"use client";

import {
  type AllHTMLAttributes,
  createElement,
  type ElementType,
  forwardRef,
} from "react";
import { composeEventHandlers } from "~/utils/compose-events";
import { type UseBoxStylesProps, useBoxStyles } from "./useBoxStyles";

export interface BoxProps
  extends Omit<UseBoxStylesProps, "component" | "cursor">,
    Omit<AllHTMLAttributes<HTMLElement>, "width" | "height" | "className"> {
  component?: ElementType;
}

const NamedBox = forwardRef<HTMLElement, BoxProps>(
  (
    {
      component = "div",
      padding,
      paddingX,
      paddingY,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      margin,
      marginX,
      marginY,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      gap,
      display,
      flexDirection,
      flexWrap,
      flexShrink,
      flexGrow,
      alignItems,
      justifyContent,
      textAlign,
      border,
      borderColor,
      borderRadius,
      background,
      boxShadow,
      // transition,
      // transform,
      height,
      width,
      position,
      pointerEvents,
      overflow,
      maxWidth,
      minWidth,
      // outline,
      className,
      onKeyDown,
      onClick,
      tabIndex,
      disabled,
      ...restProps
    }: BoxProps,
    ref,
  ) => {
    const hasCursor =
      (onClick || (tabIndex !== undefined && tabIndex >= 0)) && !disabled;

    const boxStyles = useBoxStyles({
      component,
      padding,
      paddingX,
      paddingY,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      margin,
      marginX,
      marginY,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      gap,
      display,
      flexDirection,
      flexWrap,
      flexShrink,
      flexGrow,
      alignItems,
      justifyContent,
      textAlign,
      border,
      borderColor,
      borderRadius,
      background,
      boxShadow,
      // transition,
      // transform,
      height,
      width,
      position,
      cursor: hasCursor ? "pointer" : undefined,
      pointerEvents,
      overflow,
      maxWidth,
      minWidth,
      // outline,
      className,
    });

    const handleKeyDown = composeEventHandlers(onKeyDown, (e) => {
      if (!onClick || (e.key !== "Enter" && e.key !== " ")) {
        return;
      }

      // Don't interfere with form elements you can type into
      const formTagNames = ["INPUT", "TEXTAREA", "SELECT"];
      if (e.target instanceof HTMLElement) {
        const isFormElement = formTagNames.includes(e.target.tagName);
        if (!isFormElement) {
          e.preventDefault();
          e.target.click();
        }
      }
    });

    const getTabIndex = () => {
      if (tabIndex !== undefined) {
        return tabIndex;
      }

      if (disabled) {
        return -1;
      }

      if (onClick || onKeyDown) {
        return 0;
      }

      return undefined;
    };

    return createElement(component, {
      className: boxStyles,
      ...restProps,
      onClick: disabled ? undefined : onClick,
      disabled,
      onKeyDown: handleKeyDown,
      tabIndex: getTabIndex(),
      ref,
    });
  },
);

NamedBox.displayName = "Box";

export const Box = NamedBox;
